/**
 * Ожидание цели шага (E4.6b): решение по кадрам отрисовки, без таймеров. Проверка идёт в
 * ближайшем кадре после изменения разметки; уже назначенная проверка изменением не
 * откладывается -- иначе страница, меняющая разметку каждый кадр (анимация карты),
 * никогда не давала бы проверить цель. «Нет цели» -- после двух кадров без изменений
 * разметки или после ограниченного числа кадров на устоявшейся, но непрерывно
 * анимированной странице.
 */

export interface FrameScheduler {
	request: (callback: () => void) => number;
	cancel: (id: number) => void;
}

export const BROWSER_FRAMES: FrameScheduler = {
	request: (callback) => requestAnimationFrame(callback),
	cancel: (id) => cancelAnimationFrame(id),
};

/** Сколько кадров устоявшаяся страница может меняться без цели, прежде чем цель признана отсутствующей */
export const MAX_ABSENT_FRAMES = 60;
const QUIET_FRAMES = 2;

interface WatchOptions {
	/** Цель есть на странице */
	find: () => boolean;
	/** Раздел загружен и запросы завершены */
	settled: () => boolean;
	onReady: () => void;
	onMissing: () => void;
	scheduler?: FrameScheduler;
}

export function watchTarget({
	find,
	settled,
	onReady,
	onMissing,
	scheduler = BROWSER_FRAMES,
}: WatchOptions) {
	let frame: number | null = null;
	let quiet = 0;
	let absent = 0;
	let decided = false;

	const check = () => {
		frame = null;
		if (decided || !settled()) return;
		if (find()) {
			decided = true;
			onReady();
			return;
		}
		quiet += 1;
		absent += 1;
		if (quiet >= QUIET_FRAMES || absent >= MAX_ABSENT_FRAMES) {
			decided = true;
			onMissing();
			return;
		}
		schedule();
	};

	function schedule() {
		if (frame === null && !decided) frame = scheduler.request(check);
	}

	schedule();
	return {
		/** Разметка страницы изменилась */
		changed: () => {
			quiet = 0;
			schedule();
		},
		stop: () => {
			decided = true;
			if (frame !== null) scheduler.cancel(frame);
		},
	};
}
